import React from 'react';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from '@reach/combobox';
import { formatRelative } from 'date-fns';

import '@reach/combobox/styles.css';
// import mapStyles from "./mapStyles";

const libraries = ['places'];
const mapContainerStyle = {
  height: '50vh',
  width: '55vw',
};
const options = {
  // styles: mapStyles,
  disableDefaultUI: true,
  zoomControl: true,
};
const center = {
  lat: 25.204849,
  lng: 55.270783,
};

export default function App({ handleUpdateCoordinatesApi, containerStyle, center: propCenter, zoom: propZoom, readOnly = false, address, showMarkerAtCenter = false }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyCjG7Yg6IyPXyaFjQC1KZm7FEHDkayiioY',
    libraries,
  });
  const [markers, setMarkers] = React.useState([]);
  const [selected, setSelected] = React.useState(null);

  const onMapClick = React.useCallback((e) => {
    setMarkers((current) => [
      // ...current,
      {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
        time: new Date(),
      },
    ]);
  }, []);

  const mapRef = React.useRef();
  const onMapLoad = React.useCallback((map) => {
    mapRef.current = map;
    if(address) {
       // Trigger geocoding if address is provided
       getGeocode({ address })
         .then((results) => getLatLng(results[0]))
         .then(({ lat, lng }) => {
            setMarkers([{ lat, lng, time: new Date() }]);
            map.panTo({ lat, lng });
            map.setZoom(15);
         })
         .catch((error) => {
            // console.error("Geocoding error: ", error);
         });
    } else if (propCenter) {
        map.panTo(propCenter);
        if (showMarkerAtCenter) {
            setMarkers([{ lat: propCenter.lat, lng: propCenter.lng, time: new Date() }]);
        }
    }
  }, [address, propCenter, showMarkerAtCenter]);

  const panTo = React.useCallback(({ lat, lng }) => {
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(14);
    setMarkers((current) => [
      // ...current,
      {
        lat,
        lng,
        time: new Date(),
      },
    ]);
  }, []);

  if (loadError) return 'Error';
  if (!isLoaded) return 'Loading...';

  const currentMapContainerStyle = containerStyle || mapContainerStyle;

  return (
    <div>
      {/*  <Locate panTo={panTo} /> */}
      {!readOnly && <Search panTo={panTo} />}

      <GoogleMap
        id="map"
        mapContainerStyle={currentMapContainerStyle}
        zoom={propZoom || 8}
        center={propCenter || center}
        options={options}
        onClick={!readOnly ? onMapClick : undefined}
        onLoad={onMapLoad}
      >
        {markers.map((marker) => (
          <Marker
            key={`${marker.lat}-${marker.lng}`}
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => {
              setSelected(marker);
            }}
            // icon={{
            //   url: `/bear.svg`,
            //   origin: new window.google.maps.Point(0, 0),
            //   anchor: new window.google.maps.Point(15, 15),
            //   scaledSize: new window.google.maps.Size(30, 30)
            // }}
          />
        ))}

        {selected ? (
          <InfoWindow
            position={{ lat: selected.lat, lng: selected.lng }}
            onCloseClick={() => {
              setSelected(null);
            }}
          >
            <div>
              <span className={'badge badge-soft-success text-success'}>
                Latitude : {selected.lat}
              </span>{' '}
              <br />
              <span className={'badge badge-soft-success text-success'}>
                Longitude : {selected.lng}
              </span>{' '}
              <br />
              <button
                type="button"
                onClick={() =>
                  handleUpdateCoordinatesApi(selected.lat, selected.lng)
                }
                className="btn btn-dark btn-sm waves-effect waves-light"
              >
                Update Coordinates
              </button>
              {/* <p>Spotted {formatRelative(selected.time, new Date())}</p> */}
            </div>
          </InfoWindow>
        ) : null}
      </GoogleMap>
    </div>
  );
}

function Locate({ panTo }) {
  return (
    <button
      className="locate"
      onClick={() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            panTo({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => null
        );
      }}
    >
      <i className="bx bx-compass"></i>
    </button>
  );
}

function Search({ panTo }) {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: { lat: () => 43.6532, lng: () => -79.3832 },
      radius: 100 * 1000,
    },
  });

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest

  const handleInput = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);

      panTo({ lat, lng });
    } catch (error) {}
  };

  return (
    <div
      className="search"
      style={{ position: 'absolute', left: 50, top: 20, zIndex: 999 }}
    >
      <Combobox onSelect={handleSelect}>
        <ComboboxInput
          value={value}
          onChange={handleInput}
          disabled={!ready}
          placeholder="Search your location"
        />
        <ComboboxPopover>
          <ComboboxList>
            {status === 'OK' &&
              data.map(({ id, description }) => (
                <ComboboxOption key={id} value={description} />
              ))}
          </ComboboxList>
        </ComboboxPopover>
      </Combobox>
    </div>
  );
}
